import moment from "moment";
import { SUBSCRIBER_RENEWAL_KEY } from "../../../Distributors/constants";

// ============================================================================
// CONSTANTS
// ============================================================================

/** System standard date format */
export const DATE_FORMAT = "MM/DD/YYYY";

export const FREQUENCY_LABELS = {
  1: "Monthly",
  2: "Quarterly",
  3: "Semi-Annual",
  4: "Annual",
  99: "One-Time",
};

// ============================================================================
// GENERAL UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts a value to an array
 * @param {*} value - Value to convert
 * @returns {Array} Array representation of value
 */
export function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
}

/**
 * Checks if a field name indicates a secret/sensitive field
 * @param {string} value - Field name to check
 * @returns {boolean} True if field is secret
 */
export function isSecretField(value) {
  return /password|credential|secret|key/i.test(value || "");
}

// ============================================================================
// SUBSCRIPTION MANAGEMENT HELPER FUNCTIONS
// ============================================================================

/**
 * Determines the subscription type display value based on auto-renewal status
 * @param {Object} subscription - Subscription object
 * @returns {string} Display value: "Auto-renewable" or "Non auto-renewable"
 */
export const getSubscriptionTypeDisplay = (subscription) => {
  if (!subscription) return "N/A";

  // One-time subscriptions are always non-renewable
  if (subscription?.frequency === 99) {
    return "Non auto-renewable";
  }

  // Check auto-renewal status for recurring subscriptions
  const isAutoRenewable = subscription?.isAutoRenewable;
  return isAutoRenewable ? "Auto-renewable" : "Non auto-renewable";
};

/**
 * Determines if a subscription is currently expired
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if subscription is expired
 */
export const isSubscriptionExpired = (subscription) => {
  return subscription?.isExpired || false;
};

/**
 * Determines if a subscription is currently in trial period
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if trial is active
 */
export const isSubscriptionInTrial = (subscription) => {
  return (subscription?.hasTrial && subscription?.isInTrial) || false;
};

/**
 * Determines trial label if a subscription is currently in trial period
 * @param {Object} subscription - Subscription object
 * @returns {string} "Trial" if trial is active, otherwise empty string
 */
export const determineTrialLabel = (subscription) => {
  return isSubscriptionInTrial(subscription) ? "Trial" : "";
};

/**
 * Calculates the trial start date
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Trial start date or null if no trial
 */
export const getTrialStartDate = (subscription) => {
  if (!isSubscriptionInTrial(subscription)) return null;
  const fromDate = moment(subscription?.trialStartDate);
  return fromDate;
};

/**
 * Calculates the trial end date
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Trial end date or null if no trial
 */
export const getTrialEndDate = (subscription) => {
  if (!isSubscriptionInTrial(subscription)) return null;

  const toDate = moment(subscription?.trialEndDate);
  return toDate;
};

/**
 * Determines if Renew button should be visible
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if Renew button should be shown
 */
export const shouldShowRenewButton = (subscription) => {
  if (!subscription) return false;

    // Do not show for one-time subscriptions
  if (subscription.frequency === 99 ) {
    return false;
  }

  // Show if subscription is expired
  if (isSubscriptionExpired(subscription)) {
    return true;
  }

  // Show if subscription is non auto-renewable (and not expired)
  const isAutoRenewable = subscription?.isAutoRenewable;
  if (!isAutoRenewable && !isSubscriptionExpired(subscription)) {
    return true;
  }


  return subscription?.canRenew || false;
};

/**
 * Determines if Stop button should be visible
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if Stop button should be shown
 */
export const shouldShowStopButton = (subscription) => {
  if (!subscription) return false;

  // Do not show for one-time subscriptions
  if (subscription.frequency === 99 || subscription?.isAutoRenewable === false) {
    return false;
  }

  // Do not show if subscription is expired
  if (isSubscriptionExpired(subscription)) {
    return false;
  }

  // if already stopped
  if (subscription?.isStopped) {
    return false;
  }

  // Show if subscription is still active
  return subscription?.canStop || false;
};

/**
 * Gets the effective from date, considering trial start
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Effective from date
 */
export const getEffectiveFromDate = (subscription) => {
  // If trial exists, effective from is trial start date
  if (isSubscriptionInTrial(subscription)) {
    const trialStartDate = getTrialStartDate(subscription);
    return trialStartDate || null;
  }
  // Otherwise it's the purchase date
  if (subscription?.effectiveFromDate) {
    return moment(subscription.effectiveFromDate);
  }
  return null;
};

/**
 * Gets the effective to date, considering trial period
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Effective to date
 */
export const getEffectiveToDate = (subscription) => {
  // If in trial, display trial end date
  if (isSubscriptionInTrial(subscription)) {
    const trialEndDate = getTrialEndDate(subscription);
    return trialEndDate || null;
  }

  if (subscription?.effectiveToDate) {
    return moment(subscription.effectiveToDate);
  }
  return null;
};

// ============================================================================
// CONFIGURATION HELPER FUNCTIONS
// ============================================================================

/**
 * Normalizes a configuration field
 * @param {Object} field - Field object
 * @param {number} index - Field index
 * @returns {Object} Normalized field
 */
export function normalizeConfigurationField(field, index) {
  const key =
    field?.keyName ||
    field?.key ||
    field?.fieldName ||
    `field_${index + 1}`;

  return {
    ...field,
    key,
    keyName: key,
    userLabel: field?.userLabel || field?.label || key,
    inputType: field?.inputType || (isSecretField(key) ? "secret" : "text"),
    sensitive: field?.sensitive ?? isSecretField(key),
    addTo: field?.addTo ?? 1,
  };
}

/**
 * Gets configuration values as a map
 * @param {Object} configuration - Configuration object
 * @returns {Object} Values map
 */
export function getConfigurationValuesMap(configuration) {
  if (configuration?.values) {
    return configuration.values;
  }

  return toArray(configuration?.integrationTypeAPIKey).reduce(
    (accumulator, field) => ({
      ...accumulator,
      [field?.keyName || field?.key || field?.userLabel]: field?.keyValue || "",
    }),
    {},
  );
}

/**
 * Normalizes a configuration record
 * @param {Object} configuration - Configuration object
 * @param {number} index - Configuration index
 * @returns {Object} Normalized configuration record
 */
export function normalizeConfigurationRecord(configuration, index) {
  return {
    id:
      configuration?.userAPIKeyId ||
      configuration?.id ||
      configuration?.configurationId ||
      configuration?.addInConfigurationId ||
      index + 1,
    configurationName:
      configuration?.configurationName ||
      configuration?.name ||
      `Configuration ${index + 1}`,
    values: getConfigurationValuesMap(configuration),
  };
}

/**
 * Merges manage configuration data with base add-in
 * @param {Object} baseAddIn - Base add-in object
 * @param {Object} manageResult - Manage configuration result
 * @returns {Object|null} Merged add-in data
 */
export function mergeManageConfiguration(baseAddIn, manageResult) {
  if (!manageResult && !baseAddIn) return null;

  const normalizedFields = toArray(
    manageResult?.configurationTemplate?.configurationFields,
  ).map((field, index) => normalizeConfigurationField(field, index));
  
  const requiresConfiguration =
    manageResult?.requiresConfiguration ??
    baseAddIn?.configurationRequired ??
    baseAddIn?.requiresConfiguration ??
    false;
  
  const configurations = toArray(manageResult?.configurations).map(
    (configuration, index) => normalizeConfigurationRecord(configuration, index),
  );
  
  const isConfigured = manageResult?.isConfigured ?? configurations.length > 0;

  return {
    ...(baseAddIn || undefined),
    ...(manageResult || undefined),
    name:
      manageResult?.addInName ||
      manageResult?.integrationSolutionName ||
      baseAddIn?.name,
    vendorName: manageResult?.vendorName || baseAddIn?.vendorName,
    addInTypeText: manageResult?.addInTypeText || baseAddIn?.addInTypeText,
    costType: manageResult?.costType ?? baseAddIn?.costType,
    costTypeText: manageResult?.costTypeText || baseAddIn?.costTypeText,
    configurationRequired: requiresConfiguration,
    requiresConfiguration,
    isConfigured,
    configurationStatus:
      requiresConfiguration && !isConfigured ? "missing" : "configured",
    configurationDefinition: {
      fields: normalizedFields,
    },
    configurations,
    accessGrantedBy: manageResult?.accessGrantedBy || baseAddIn?.accessGrantedBy || null,
  };
}

/**
 * Gets managed field definitions from add-in
 * @param {Object} addIn - Add-in object
 * @returns {Array} Field definitions
 */
export function getManagedFieldDefinitions(addIn) {
  return toArray(addIn?.configurationDefinition?.fields);
}

/**
 * Gets managed field value from form data
 * @param {Object} field - Field definition
 * @param {Object} formData - Form data
 * @param {Object} existingValues - Existing values
 * @returns {string} Field value
 */
export function getManagedFieldValue(field, formData, existingValues = {}) {
  const directFieldValue =
    formData?.fields?.[field?.keyName] ??
    formData?.fields?.[field?.key] ??
    formData?.fields?.[field?.userLabel];

  if (directFieldValue !== undefined && directFieldValue !== null) {
    return String(directFieldValue);
  }

  const normalizedLabel = `${field?.userLabel || ""} ${field?.key || ""}`.toLowerCase();

  if (normalizedLabel.includes("agency")) return formData.agencyName;
  if (normalizedLabel.includes("username") || normalizedLabel.includes("user name")) {
    return formData.username;
  }
  if (isSecretField(normalizedLabel)) return formData.password;

  return existingValues[field?.key] || existingValues[field?.keyName] || "";
}

/**
 * Builds configuration payload for API
 * @param {number} addInId - Add-in ID
 * @param {Object} formData - Form data
 * @param {Array} fieldDefinitions - Field definitions
 * @param {number} configurationId - Configuration ID (optional)
 * @param {Object} existingValues - Existing values (optional)
 * @returns {Object} Configuration payload
 */
export function buildConfigurationPayload(addInId, formData, fieldDefinitions, configurationId, existingValues = {}) {
  const configurationValues = fieldDefinitions.map((field) => ({
    keyName: field.keyName || field.key,
    userLabel: field.userLabel,
    hasKeyValue: true,
    keyValue: getManagedFieldValue(field, formData, existingValues),
    addTo: field.addTo ?? 1,
  }));

  return {
    addInId,
    configurationName: formData.configurationName.trim(),
    ...(configurationId ? { configurationId } : {}),
    configurationValues,
  };
}

// ============================================================================
// SUBSCRIPTION DATA HELPER FUNCTIONS
// ============================================================================

/**
 * Normalizes a subscription object
 * @param {Object} subscription - Subscription object
 * @returns {Object} Normalized subscription
 */
export function normalizeSubscription(subscription) {
  if (!subscription) return subscription;

  return {
    ...subscription,
    frequencyText:
      subscription.frequencyText ||
      FREQUENCY_LABELS[subscription.frequency] ||
      (subscription.isRecurring === false ? "One-Time" : "N/A"),
    effectiveFrom:
      subscription.effectiveFrom || subscription.effectiveFromDate || null,
    effectiveTo: subscription.effectiveTo || subscription.effectiveToDate || null,
    isSubscribed:
      subscription.isSubscribed ??
      subscription.isPurchased ??
      subscription.isAssociated ??
      false,
  };
}

/**
 * Gets normalized subscriptions from add-in
 * @param {Object} addIn - Add-in object
 * @returns {Array} Normalized subscriptions
 */
export function getNormalizedSubscriptions(addIn) {
  let rawSubscriptions = [];

  if (Array.isArray(addIn?.availableSubscriptions)) {
    rawSubscriptions = addIn.availableSubscriptions;
  } else if (Array.isArray(addIn?.subscriptions)) {
    rawSubscriptions = addIn.subscriptions;
  }

  return rawSubscriptions.map(normalizeSubscription);
}

/**
 * Gets subscription lifecycle status
 * @param {Object} addIn - Add-in object
 * @param {Array} subscriptions - Subscriptions array
 * @returns {string} Lifecycle status
 */
export function getSubscriptionLifecycle(addIn, subscriptions) {
  if (addIn?.subscriptionLifecycle) return addIn.subscriptionLifecycle;

  const activeSubscription =
    subscriptions.find((subscription) => subscription.isSubscribed) ||
    subscriptions[0] ||
    null;

  if (!activeSubscription) return "active";
  if (activeSubscription.isExpired) return "expired";
  if (activeSubscription.enableTrial) return "trial-active";
  return "active";
}

/**
 * Merges add-in data from detail and fallback sources
 * @param {Object} detailAddIn - Detail add-in data
 * @param {Object} fallbackAddIn - Fallback add-in data
 * @returns {Object|null} Merged add-in data
 */
export function mergeAddInData(detailAddIn, fallbackAddIn) {
  if (!detailAddIn && !fallbackAddIn) return null;

  const mergedAddIn = {
    ...(fallbackAddIn || undefined),
    ...(detailAddIn || undefined),
  };

  const subscriptions = getNormalizedSubscriptions(mergedAddIn);

  return {
    ...mergedAddIn,
    availableSubscriptions: subscriptions,
    configurationDefinition:
      detailAddIn?.configurationDefinition ||
      fallbackAddIn?.configurationDefinition,
    configurations: detailAddIn?.configurations || fallbackAddIn?.configurations,
    configurationStatus:
      detailAddIn?.configurationStatus || fallbackAddIn?.configurationStatus,
    subscriptionLifecycle: getSubscriptionLifecycle(mergedAddIn, subscriptions),
  };
}

/**
 * Maps add-in data to details format
 * @param {Object} addIn - Add-in object
 * @returns {Object} Mapped add-in details
 */
export function getMappedDetailsAddIn(addIn) {
  const subscriptions = getNormalizedSubscriptions(addIn);
  const activeSub =
    subscriptions.find((sub) => sub.isSubscribed) || subscriptions[0] || null;

  const apiConfigFields = getManagedFieldDefinitions(addIn);
  const apiConfigurations = Array.isArray(addIn?.configurations)
    ? addIn.configurations
    : [];
  const configurationRequired =
    addIn?.configurationRequired ??
    addIn?.requiresConfiguration ??
    addIn?.configurationStatus === "missing";

  return {
    ...addIn,
    configurationRequired,
    isConfigured: addIn?.isConfigured ?? apiConfigurations.length > 0,
    configurationDefinition: {
      fields: apiConfigFields,
    },
    configurations: apiConfigurations,
    subscriptionLifecycle: addIn?.subscriptionLifecycle || "active",
    accessGrantedBy: addIn?.accessGrantedBy || null,
    subscription: {
      isRecurring: activeSub?.isRecurring ?? false,
      frequencyText: activeSub?.frequencyText || (addIn?.costType === 1 ? "One-Time" : "N/A"),
      effectiveFrom: activeSub?.effectiveFrom || null,
      effectiveTo: activeSub?.effectiveTo || null,
      trialDurationDays: activeSub?.trialDurationDays || null,
      price: activeSub?.actualValue ?? activeSub?.baseValue ?? null,
      trialMessage:
        activeSub?.enableTrial && activeSub?.trialDurationDays
          ? `${activeSub.trialDurationDays} Days Trial, then $${
              activeSub.actualValue ?? activeSub.baseValue ?? 0
            } ${activeSub.frequencyText || ""}`
          : "N/A",
    },
  };
}

// ============================================================================
// SUBSCRIBER RENEWAL LOCALSTORAGE HELPER FUNCTIONS
// ============================================================================

/**
 * Gets all subscriber renewal IDs from localStorage
 * @returns {Array} Array of renewal objects with addInsId
 */
export const getSubscriberRenewalIds = () => {
  try {
    return JSON.parse(localStorage.getItem(SUBSCRIBER_RENEWAL_KEY)) || [];
  } catch (error) {
    console.error("Error parsing subscriber renewal IDs:", error);
    return [];
  }
};

/**
 * Adds a subscriber renewal ID to localStorage (avoids duplicates)
 * @param {number} addInsId - The add-in ID to add
 * @returns {Array} Updated array of renewal IDs
 */
export const addSubscriberRenewalId = (addInsId) => {
  const renewalIds = getSubscriberRenewalIds();

  const exists = renewalIds.some((item) => item.addInsId === addInsId);

  if (!exists) {
    renewalIds.push({ addInsId });
    localStorage.setItem(SUBSCRIBER_RENEWAL_KEY, JSON.stringify(renewalIds));
  }

  return renewalIds;
};

/**
 * Removes a subscriber renewal ID from localStorage
 * @param {number} addInsId - The add-in ID to remove
 * @returns {Array} Updated array of renewal IDs
 */
export const removeSubscriberRenewalId = (addInsId) => {
  let renewalIds = getSubscriberRenewalIds();

  renewalIds = renewalIds.filter((item) => item.addInsId !== addInsId);

  localStorage.setItem(SUBSCRIBER_RENEWAL_KEY, JSON.stringify(renewalIds));
  return renewalIds;
};

/**
 * Checks if a subscriber add-in ID exists in localStorage renewals
 * @param {number} addInsId - The add-in ID to check
 * @returns {boolean} True if the add-in is selected for renewal
 */
export const isSubscriberAddInsSelected = (addInsId) => {
  const renewalIds = getSubscriberRenewalIds();

  return renewalIds.some((item) => item.addInsId === addInsId);
};

/**
 * Checks if any cart items exist in subscriber renewal localStorage
 * @param {Array} cartItems - Array of cart items
 * @returns {boolean} True if any cart item exists in localStorage
 */
export const isSubscriberCartAddInExistsInLocalStorage = (cartItems = []) => {
  if (!cartItems.length) return false;

  return cartItems.some((item) =>
    isSubscriberAddInsSelected(item?.addIn?.addInId)
  );
};

/**
 * Clears all subscriber renewal IDs from localStorage
 */
export const clearSubscriberRenewalIds = () => {
  localStorage.removeItem(SUBSCRIBER_RENEWAL_KEY);
};
